/**
 * System Maintenance Tab
 * Consolidated maintenance tools for the Admin Panel
 * 
 * Includes:
 * - Database Maintenance (from ExamConfigManager)
 * - Pipeline Monitoring
 * - Performance Dashboard
 * - Canonical ID Migration
 */
import React, { useState, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { API } from '@/App';
import {
  Wrench,
  Database,
  Activity,
  BarChart3,
  RefreshCw,
  Settings,
  Layers,
  Target,
  FileText,
  CheckCircle,
  AlertTriangle,
  Trash2,
  Link
} from 'lucide-react';

// Import sub-components
import PipelineStatusTracker from './PipelineStatusTracker';
import PerformanceDashboard from './PerformanceDashboard';
import { CanonicalIdMigrationDashboard } from './index';

const SystemMaintenanceTab = ({ getAuthHeaders }) => {
  const [activeSubTab, setActiveSubTab] = useState('database');
  const [loading, setLoading] = useState(false);

  const fetchWithAuth = useCallback(async (endpoint, options = {}) => {
    const headers = getAuthHeaders();
    const response = await fetch(`${API}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...headers,
        ...options.headers,
      },
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || 'Request failed');
    }
    return response.json();
  }, [getAuthHeaders]);

  return (
    <div className="space-y-6" data-testid="system-maintenance-tab">
      {/* Header */}
      <Card className="border-orange-200 bg-gradient-to-br from-orange-50/50 to-amber-50/50">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-orange-700">
            <Wrench className="w-6 h-6" />
            System-Wartung & Diagnose
          </CardTitle>
          <CardDescription>
            Datenbank-Wartung, Pipeline-Überwachung, Performance-Monitoring und Migrationen
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Sub-Tabs */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="space-y-4">
        <TabsList className="bg-slate-100 grid grid-cols-4 w-full max-w-2xl">
          <TabsTrigger value="database" className="text-xs">
            <Database className="w-3 h-3 mr-1" />
            Datenbank
          </TabsTrigger>
          <TabsTrigger value="pipeline" className="text-xs">
            <Activity className="w-3 h-3 mr-1" />
            Pipeline
          </TabsTrigger>
          <TabsTrigger value="performance" className="text-xs">
            <BarChart3 className="w-3 h-3 mr-1" />
            Performance
          </TabsTrigger>
          <TabsTrigger value="migration" className="text-xs">
            <RefreshCw className="w-3 h-3 mr-1" />
            Migration
          </TabsTrigger>
        </TabsList>

        {/* Database Maintenance Tab */}
        <TabsContent value="database" className="space-y-4">
          <Card className="border-orange-200">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-orange-700 text-lg">
                <Database className="w-5 h-5" />
                Datenbank-Wartung
              </CardTitle>
              <CardDescription>
                Bereinigen Sie Duplikate und veraltete Einträge in der Datenbank.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Exam Cleanup */}
              <div className="p-4 border rounded-lg bg-slate-50">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Settings className="w-4 h-4 text-orange-600" />
                  Exam-Daten
                </h4>
                <div className="flex flex-wrap gap-3">
                  <Button
                    variant="outline"
                    className="border-orange-300 text-orange-700 hover:bg-orange-50"
                    onClick={async () => {
                      try {
                        setLoading(true);
                        const response = await fetchWithAuth('/question-engine/cleanup-exams', {
                          method: 'POST'
                        });
                        toast.success(response.message || 'Exam-Duplikate bereinigt');
                      } catch (error) {
                        toast.error('Fehler: ' + error.message);
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                    data-testid="cleanup-exams-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Exam-Duplikate bereinigen
                  </Button>
                </div>
              </div>

              {/* AKU Maintenance */}
              <div className="p-4 border rounded-lg bg-slate-50">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Layers className="w-4 h-4 text-blue-600" />
                  AKU-Daten
                </h4>
                <div className="flex flex-wrap gap-3">
                  <Button
                    variant="outline"
                    className="border-blue-300 text-blue-700 hover:bg-blue-50"
                    onClick={async () => {
                      try {
                        setLoading(true);
                        const response = await fetchWithAuth('/aku/repair-all', {
                          method: 'POST'
                        });
                        toast.success(`Repariert: ${response.fixed_category} Kategorien, ${response.fixed_certification} Zertifizierungen`);
                      } catch (error) {
                        toast.error('Fehler: ' + error.message);
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                    data-testid="repair-aku-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    AKU-Daten reparieren
                  </Button>

                  <Button
                    variant="outline"
                    className="border-purple-300 text-purple-700 hover:bg-purple-50"
                    onClick={async () => {
                      try {
                        const status = await fetchWithAuth('/question-engine/safe-aku-status');
                        const confirmMsg = `SAFe AKU-Status:\n\n` +
                          `• Generische SAFe: ${status.generic_safe_akus}\n` +
                          `• SAFe Agilist (SA): ${status.safe_agilist_akus}\n` +
                          `• SAFe Practitioner (SP): ${status.safe_practitioner_akus}\n` +
                          `• Veraltete 'Leading SAFe': ${status.leading_safe_akus_deprecated || 0}\n\n` +
                          `Diese Aktion:\n` +
                          `1. Dupliziert generische SAFe-AKUs auf beide Zertifizierungen\n` +
                          `2. Migriert 'Leading SAFe' AKUs zu 'SAFe Agilist'\n` +
                          `3. Löscht die Originale nach der Migration\n\n` +
                          `Fortfahren?`;
                        
                        if (!window.confirm(confirmMsg)) return;
                        
                        setLoading(true);
                        const response = await fetchWithAuth('/question-engine/migrate-safe-akus?delete_original=true', {
                          method: 'POST'
                        });
                        toast.success(response.message || `${response.duplicated} AKUs migriert`);
                      } catch (error) {
                        toast.error('Fehler: ' + error.message);
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                    data-testid="migrate-safe-aku-btn"
                  >
                    <Target className="w-4 h-4 mr-2" />
                    SAFe-AKUs bereinigen
                  </Button>

                  {/* P2: AKUs Migration */}
                  <Button
                    variant="outline"
                    className="border-purple-300 text-purple-700 hover:bg-purple-50"
                    onClick={async () => {
                      try {
                        setLoading(true);
                        toast.info('Prüfe Migrations-Status...');
                        const data = await fetchWithAuth('/admin/migration/akus-status');
                        
                        let message = `📦 AKUs Migration Status\n\n`;
                        message += `Legacy AKUs: ${data.legacy_akus.total}\n`;
                        message += `V2 AKUs: ${data.akus_v2.total}\n`;
                        message += `Bereits migriert: ${data.akus_v2.migrated_from_legacy}\n`;
                        message += `Ausstehend: ${data.migration_status.pending}\n\n`;
                        
                        if (data.legacy_akus.total > 0 && Object.keys(data.legacy_akus.by_certification).length > 0) {
                          message += `Legacy nach Zertifizierung:\n`;
                          Object.entries(data.legacy_akus.by_certification).forEach(([cert, count]) => {
                            message += `  • ${cert}: ${count}\n`;
                          });
                        }
                        
                        alert(message);
                        
                        if (data.migration_status.pending > 0) {
                          toast.warning(`${data.migration_status.pending} AKUs ausstehend`);
                        } else if (data.legacy_akus.total === 0) {
                          toast.success('Keine Legacy-AKUs vorhanden');
                        } else {
                          toast.success('Migration vollständig');
                        }
                      } catch (error) {
                        toast.error('Fehler: ' + error.message);
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                    data-testid="akus-migration-status-btn"
                  >
                    <Database className="w-4 h-4 mr-2" />
                    AKUs Migration
                  </Button>
                </div>
              </div>

              {/* Diagnostic Tools */}
              <div className="p-4 border rounded-lg bg-slate-50">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-green-600" />
                  Diagnose
                </h4>
                <div className="flex flex-wrap gap-3">
                  {/* P2: Course Mapping Health Check */}
                  <Button
                    variant="outline"
                    className="border-teal-300 text-teal-700 hover:bg-teal-50"
                    onClick={async () => {
                      try {
                        setLoading(true);
                        toast.info('Prüfe Datenintegrität...');
                        const data = await fetchWithAuth('/admin/health/course-mapping');
                        
                        let message = `🔍 Course Mapping Health Check\n\n`;
                        message += `Status: ${data.health_status}\n`;
                        message += `Gesamtprobleme: ${data.total_issues}\n\n`;
                        
                        if (data.total_issues > 0) {
                          message += `Nach Schweregrad:\n`;
                          message += `  HIGH: ${data.by_severity.HIGH}\n`;
                          message += `  MEDIUM: ${data.by_severity.MEDIUM}\n`;
                          message += `  LOW: ${data.by_severity.LOW}\n\n`;
                          
                          message += `Details:\n`;
                          Object.entries(data.issues).forEach(([key, issue]) => {
                            if (issue.count > 0) {
                              message += `  • ${key}: ${issue.count} (${issue.severity})\n`;
                            }
                          });
                        } else {
                          message += `✅ Alle Mappings sind gesund!`;
                        }
                        
                        alert(message);
                        if (data.health_status === 'HEALTHY') {
                          toast.success('Alle Mappings OK');
                        } else if (data.health_status === 'CRITICAL') {
                          toast.error(`${data.by_severity.HIGH} kritische Probleme gefunden`);
                        } else {
                          toast.warning(`${data.total_issues} Probleme gefunden`);
                        }
                      } catch (error) {
                        toast.error('Fehler: ' + error.message);
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                    data-testid="course-mapping-health-btn"
                  >
                    <Link className="w-4 h-4 mr-2" />
                    Mapping-Integrität
                  </Button>

                  <Button
                    variant="outline"
                    className="border-green-300 text-green-700 hover:bg-green-50"
                    onClick={async () => {
                      try {
                        const data = await fetchWithAuth('/aku/debug-file-storage');
                        
                        let message = `📁 PDF-Speicher Status\n\n`;
                        message += `MongoDB Collections: ${data.collections?.length || 0}\n`;
                        message += `Speicher-Einträge: ${data.kb_file_storage_count || 0}\n`;
                        message += `Datei-Chunks: ${data.kb_file_chunks_count || 0}\n\n`;
                        
                        message += `✅ Dokumente MIT PDF (${data.documents_with_files?.length || 0}):\n`;
                        (data.documents_with_files || []).forEach(doc => {
                          message += `  • ${doc.filename} (${doc.mongodb_chunks} chunks)\n`;
                        });
                        
                        message += `\n❌ Dokumente OHNE PDF (${data.documents_without_files?.length || 0}):\n`;
                        (data.documents_without_files || []).forEach(doc => {
                          message += `  • ${doc.filename}\n`;
                        });
                        
                        if (data.errors?.length > 0) {
                          message += `\n🔴 Fehler:\n`;
                          data.errors.forEach(err => {
                            message += `  • ${err}\n`;
                          });
                        }
                        
                        alert(message);
                      } catch (error) {
                        toast.error('Fehler: ' + error.message);
                      }
                    }}
                    data-testid="pdf-storage-check-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    PDF-Speicher prüfen
                  </Button>

                  <Button
                    variant="outline"
                    className="border-slate-300 text-slate-700 hover:bg-slate-100"
                    onClick={async () => {
                      try {
                        toast.info('Teste MongoDB-Verbindung...');
                        const data = await fetchWithAuth('/health/mongodb');
                        
                        let message = `🔧 MongoDB Test\n\n`;
                        message += `Verbindung: ${data.connected ? '✅ OK' : '❌ Fehlgeschlagen'}\n`;
                        message += `Datenbank: ${data.database || 'N/A'}\n`;
                        message += `Collections: ${data.collections_count || 0}\n`;
                        
                        if (data.test_results) {
                          message += `\nTest-Ergebnisse:\n`;
                          Object.entries(data.test_results).forEach(([key, value]) => {
                            message += `  ${key}: ${value ? '✅' : '❌'}\n`;
                          });
                        }
                        
                        alert(message);
                        toast.success('MongoDB-Test abgeschlossen');
                      } catch (error) {
                        toast.error('MongoDB Test fehlgeschlagen: ' + error.message);
                      }
                    }}
                    data-testid="mongodb-test-btn"
                  >
                    <Database className="w-4 h-4 mr-2" />
                    MongoDB Test
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pipeline Monitoring Tab */}
        <TabsContent value="pipeline" className="space-y-4">
          <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50/50 to-blue-50/50 mb-4">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-indigo-700">
                <Activity className="w-5 h-5" />
                Pipeline-Überwachung
              </CardTitle>
              <CardDescription>
                Echtzeit-Status aller laufenden und kürzlich abgeschlossenen Pipelines
              </CardDescription>
            </CardHeader>
          </Card>
          <PipelineStatusTracker 
            showActiveOnly={false}
            autoRefresh={true}
            refreshInterval={2000}
            maxJobs={10}
          />
        </TabsContent>

        {/* Performance Dashboard Tab */}
        <TabsContent value="performance" className="space-y-4">
          <PerformanceDashboard key="performance-maintenance" isActive={activeSubTab === 'performance'} />
        </TabsContent>

        {/* Migration Tab */}
        <TabsContent value="migration" className="space-y-4">
          <CanonicalIdMigrationDashboard key="migration-maintenance" getAuthHeaders={getAuthHeaders} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SystemMaintenanceTab;
